import React, { useEffect, useMemo, useState } from 'react';
import {
  BodyTextSmall,
  DeprecatedButtonGroup,
  HeadingText20,
  HeadingText30,
  HeadingText50,
  RadioGroup,
  SelectField,
} from '@modernhealth/vital-components';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CoachingVisitModalityType, CoachingVisitState } from 'src/generated/webapp_gql';
import { PopoverPosition } from 'src/components/Clients/Popover';
import {
  GetMemberInformationDocument,
  GetMemberInformationQuery,
  GetMemberInformationQueryVariables,
} from 'src/components/MemberProfileDrawer/documents/GetMemberInformation.generated';

import type { InvoicingPageStepProps } from '../types';
import { QuestionBubble } from '../../Clients/QuestionBubble';
import { useFormatDateTime } from '../hooks/useFormatDateTime';
import { SimplifiedCoachingVisitNodeFragment } from '../documents/SimplifiedCoachingVisitNode.generated';
import {
  GetCoachingVisitQuery,
  GetCoachingVisitDocument,
  GetCoachingVisitQueryVariables,
} from '../documents/getCoachingVisit.generated';
import {
  GetCoachingVisitsForMemberQuery,
  GetCoachingVisitsForMemberDocument,
  GetCoachingVisitsForMemberQueryVariables,
} from '../documents/getCoachingVisitsForMember.generated';

export enum SessionInformationTestID {
  Container = 'SessionInformationTestID.Container',
  ClinicallyIndicatedQuestion = 'SessionInformationTestID.ClinicallyIndicatedQuestion',
  ClientNameInput = 'SessionInformationTestID.ClientNameInput',
  SessionDateSelector = 'SessionInformationTestID.SessionDateSelector',
  SessionTypeRadioGroup = 'SessionInformationTestID.SessionTypeRadioGroup',
  AttendanceRadioGroup = 'SessionInformationTestID.AttendanceRadioGroup',
  ModalityRadioGroup = 'SessionInformationTestID.ModalityRadioGroup',
  CancelButton = 'SessionInformationTestID.CancelButton',
  NextButton = 'SessionInformationTestID.NextButton',
}

export const SessionInformation: React.FC<InvoicingPageStepProps> = ({
  data,
  setData,
  nextStep,
  memberClient,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const styles = useStyles();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { pammMemberScheduling } = useFlags();

  const [members, setMembers] = useState<{ label: string; value: string }[]>([]);
  const [visits, setVisits] = useState<
    { label: string; value: string; data: SimplifiedCoachingVisitNodeFragment }[]
  >([]);

  const { search } = useLocation();
  const params = useMemo<URLSearchParams>(() => new URLSearchParams(search), [search]);
  const visitUuid = useMemo<string | null>(() => params.get('visitUuid'), [params]);
  const memberUuid = useMemo<string | null>(() => params.get('memberUuid'), [params]);
  const formatDate = useFormatDateTime();

  const canContinue = useMemo<boolean>(
    () =>
      Boolean(data.visit) &&
      Boolean(data.visit?.state !== CoachingVisitState.Scheduled) &&
      Boolean(data.pammIsClinicallyIndicatedForTheClient !== null),
    [data],
  );

  useEffect(() => {
    (async () => {
      if (visitUuid) {
        setIsLoading(true);
        const { data } = await memberClient.query<
          GetCoachingVisitQuery,
          GetCoachingVisitQueryVariables
        >({
          query: GetCoachingVisitDocument,
          variables: { visitUuid },
        });

        const visit = data.coachingVisit;
        const member = visit?.coachAssignment?.member;

        if (visit && member) {
          setMembers([{ value: member.uuid, label: member.fullName! }]);
          setVisits([{ value: visit.uuid, label: formatDate(visit.scheduledTime), data: visit }]);
          setData((data) => ({ ...data, visit: data.visit || visit }));
        }
        setIsLoading(false);
      }

      if (memberUuid) {
        setIsLoading(true);
        const { data: memberData } = await memberClient.query<
          GetMemberInformationQuery,
          GetMemberInformationQueryVariables
        >({
          query: GetMemberInformationDocument,
          variables: { uuid: memberUuid },
        });

        const member = memberData.member;
        let fullName = '';
        if (member) {
          fullName = `${member.firstName!} ${member.lastName!}`;
          setMembers([{ value: member.uuid, label: fullName! }]);

          const { data: coachingVisitForMemberData } = await memberClient.query<
            GetCoachingVisitsForMemberQuery,
            GetCoachingVisitsForMemberQueryVariables
          >({
            query: GetCoachingVisitsForMemberDocument,
            variables: { memberUuid },
          });

          const visits = coachingVisitForMemberData.coachingVisitToReport;

          if (visits?.length) {
            setVisits(
              visits.map((visit) => ({
                value: visit!.uuid,
                label: formatDate(visit!.scheduledTime),
                data: visit!,
              })),
            );
          } else {
            enqueueSnackbar(`There are currently no valid sessions to report for ${fullName}`, {
              variant: 'error',
            });
          }
        }

        setIsLoading(false);
      }
    })();
  }, [visitUuid, memberUuid]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center">
        <CircularProgress />;
      </div>
    );
  }

  return (
    <div data-testid={SessionInformationTestID.Container}>
      <div className={styles.header}>
        <HeadingText50>Invoice submission</HeadingText50>
        <BodyTextSmall>
          We collect this information for the purposes of billing. Modern Health's Finance team will
          process payment within the next 7-14 business days. If you have questions, please reach
          out to <a href="mailto:provider@joinmodernhealth.com">provider@joinmodernhealth.com</a>.
        </BodyTextSmall>
      </div>

      <SelectField
        isDisabled={Boolean(visitUuid || memberUuid)}
        fieldId="clientName"
        data-testid={SessionInformationTestID.ClientNameInput}
        label="Client name"
        placeholder="Select client name"
        options={members}
        value={memberUuid ?? data.visit?.coachAssignment?.member?.uuid}
      />

      <div className={styles.form}>
        <HeadingText30>Session information</HeadingText30>
        <SelectField
          isDisabled={Boolean(visitUuid)}
          fieldId="dateAndTime"
          data-testid={SessionInformationTestID.SessionDateSelector}
          label="Session date and time"
          placeholder="Select session date and time"
          options={visits}
          value={data.visit?.uuid}
          onChange={(option) => setData((data) => ({ ...data, visit: option!.data }))}
        />
        {Boolean(data.visit) ? (
          <>
            <RadioGroup
              testID={SessionInformationTestID.AttendanceRadioGroup}
              legend={
                <div className="flex items-center">
                  <HeadingText20 className="m-0 mr-2">Attendance</HeadingText20>
                  <QuestionBubble
                    popoverTitle="Attendance"
                    sections={[
                      {
                        sectionTitle: 'Attended',
                        sectionBody: 'The client attended the session during this time',
                      },
                      {
                        sectionTitle: 'Late cancellation',
                        sectionBody:
                          'The client cancelled the session 48 hours before the session scheduled time',
                      },
                      {
                        sectionTitle: 'No show',
                        sectionBody: 'The client did not show up to the session',
                      },
                    ]}
                  />
                </div>
              }
              options={[
                { label: 'Attended', value: CoachingVisitState.Completed },
                { label: 'Late cancellation', value: CoachingVisitState.LateCancellation },
                { label: 'No show', value: CoachingVisitState.NoShow },
              ]}
              selected={data.visit?.state}
              onChange={(value) =>
                setData((data) => ({
                  ...data,
                  visit: data.visit ? { ...data.visit, state: value as CoachingVisitState } : null,
                }))
              }
            />
            <RadioGroup
              testID={SessionInformationTestID.ModalityRadioGroup}
              legend={<HeadingText20 className="m-0 mr-2">Modality</HeadingText20>}
              options={[
                { label: 'In-person', value: CoachingVisitModalityType.InPerson },
                { label: 'Video', value: CoachingVisitModalityType.Video },
              ]}
              selected={data.visit?.modalityType}
              onChange={(value) =>
                setData((data) => ({
                  ...data,
                  visit: data.visit
                    ? { ...data.visit, modalityType: value as CoachingVisitModalityType }
                    : null,
                }))
              }
            />
            <RadioGroup
              testID={SessionInformationTestID.ClinicallyIndicatedQuestion}
              legend={
                <>
                  <div className="flex items-center">
                    <HeadingText20 className="m-0 mr-2">Clinical assessment</HeadingText20>
                    {pammMemberScheduling && (
                      <QuestionBubble
                        popoverPosition={PopoverPosition.Right}
                        popoverTitle="Clinically Indicated"
                        sections={[
                          {
                            sectionBody:
                              'If clinically indicated, follow-up sessions are recommended to both the provider and client. If not, you will be unmatched with the client.',
                          },
                        ]}
                      />
                    )}
                  </div>
                  <div className="flex items-center mt-6">
                    <HeadingText20 className="m-0 mr-2">
                      Psychiatry and medication management is a clinically indicated option for the
                      client.
                    </HeadingText20>
                  </div>
                </>
              }
              options={[
                { label: 'Yes', value: 'YES' },
                { label: 'No', value: 'NO' },
              ]}
              selected={
                data.pammIsClinicallyIndicatedForTheClient !== null
                  ? data.pammIsClinicallyIndicatedForTheClient
                    ? 'YES'
                    : 'NO'
                  : undefined
              }
              onChange={(value) =>
                setData((data) => ({
                  ...data,
                  pammIsClinicallyIndicatedForTheClient: value === 'YES',
                }))
              }
            />
          </>
        ) : null}
      </div>
      <DeprecatedButtonGroup
        firstButtonText="Cancel"
        firstButtonTestId={SessionInformationTestID.CancelButton}
        onFirstButtonClick={history.goBack}
        secondButtonText="Next"
        secondButtonTestId={SessionInformationTestID.NextButton}
        onSecondButtonClick={nextStep}
        secondButtonDisabled={!canContinue}
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    '& > *': {
      margin: 0,
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    '& > *': {
      margin: 0,
    },
  },
}));
